import { useRef } from 'react';
import type { ReactNode } from 'react';
import type { Route } from '../routes/routes';
import './Rail.css';

interface RailProps {
  brand: string;
  routes: readonly Route[];
  labels: Record<Route, string>;
  active: Route;
  onSelect: (route: Route) => void;
  /** Optional content rendered at the bottom of the rail (e.g. the License panel). */
  footer?: ReactNode;
}

/**
 * Left navigation rail (spec §8). The two modes (cue-forge-a26) are exposed as an
 * ARIA tablist so keyboard users get roving focus and screen readers announce the
 * active mode. There are no Pro-locked routes any more — every Pro feature gates
 * inline within Studio or Convert (spec §7.5).
 */
export function Rail({ brand, routes, labels, active, onSelect, footer }: RailProps) {
  const tabRefs = useRef(new Map<Route, HTMLButtonElement>());

  // Roving-tabindex keyboard support: arrows move focus with wrap-around,
  // Home/End jump, and selection follows focus (the simpler ARIA tabs pattern).
  // Left/Right are accepted alongside Up/Down even though the rail is vertical.
  function onTablistKeyDown(e: React.KeyboardEvent) {
    const current = routes.indexOf(active);
    let next: number;
    switch (e.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = (current + 1) % routes.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        next = (current - 1 + routes.length) % routes.length;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = routes.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    const route = routes[next];
    if (route === undefined) return; // unreachable for a non-empty rail
    onSelect(route);
    tabRefs.current.get(route)?.focus();
  }

  return (
    <nav className="cf-rail" aria-label="Primary">
      <div className="cf-rail__brand">{brand}</div>
      <div
        className="cf-rail__tabs"
        role="tablist"
        aria-orientation="vertical"
        onKeyDown={onTablistKeyDown}
      >
        {routes.map((route) => {
          const selected = route === active;
          return (
            <button
              key={route}
              ref={(el) => {
                if (el) tabRefs.current.set(route, el);
                else tabRefs.current.delete(route);
              }}
              id={`cf-tab-${route}`}
              type="button"
              role="tab"
              aria-selected={selected}
              // Both panels stay mounted (the inactive one is `hidden`,
              // cue-forge-drt.14), so every tab can reference its panel.
              aria-controls={`cf-panel-${route}`}
              tabIndex={selected ? 0 : -1}
              className="cf-rail__tab"
              onClick={() => onSelect(route)}
            >
              {labels[route]}
            </button>
          );
        })}
      </div>
      {footer && <div className="cf-rail__footer">{footer}</div>}
    </nav>
  );
}
